(function () {
  'use strict';

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const CONTROLLER_KEY = Symbol.for('tbc.cartDrawer.controller');
  const REQUEST_TIMEOUT_MS = 15000;

  function cartSignature(cart) {
    return JSON.stringify({ currency: cart.currency || '', count: cart.item_count, total: cart.total_price, original: cart.original_total_price, discount: cart.total_discount,
      cartDiscounts: cart.cart_level_discount_applications || [], items: cart.items.map(function (item) {
        return { key: item.key, id: variantId(item), product: productId(item), quantity: item.quantity, title: item.product_title || item.title,
          price: item.price, original: item.original_price, final: item.final_price, line: item.line_price, originalLine: item.original_line_price, finalLine: item.final_line_price,
          properties: item.properties || {}, sellingPlan: item.selling_plan_allocation || null, discounts: item.discount_allocations || item.line_level_discount_allocations || item.discounts || [],
          image: typeof item.image === 'string' ? item.image : item.image && (item.image.src || item.image.url), shipping: item.requires_shipping,
          instructions: item.instructions || null, rule: item.quantity_rule || null, parent: item.parent_relationship || null, components: item.item_components || null };
      }) });
  }

  function sectionContext(location, template) {
    const url = new URL(location.href);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    url.searchParams.set('view', template || 'danielmogs2');
    return url;
  }

  function cartValue(value) {
    const cart = value && (value.cart || value);
    if (!cart || !Array.isArray(cart.items)) throw new Error('The cart response is incomplete. Please retry.');
    return cart;
  }

  function variantId(item) {
    return String(item.variant_id || item.id);
  }

  function productId(item) {
    return String(item.product_id || item.product && item.product.id || '');
  }

  function lineMetadata(item, metadata) {
    const entries = Array.isArray(metadata) ? metadata.filter(function (entry) { return entry && typeof entry === 'object'; }) : [];
    return entries.find(function (entry) { return entry.key === item.key; }) || entries.find(function (entry) {
      return String(entry.variantId) === variantId(item) && (!productId(item) || !entry.productId || String(entry.productId) === productId(item));
    });
  }

  function shippableItem(item, metadata) {
    if (item.quantity <= 0) return false;
    const entry = lineMetadata(item, metadata);
    if (entry && typeof entry.shippable === 'boolean') return entry.shippable;
    if (entry && ['ebook', 'audio', 'digital', 'protection'].includes(entry.kind)) return false;
    if (item.requires_shipping !== true) return false;
    const type = item.product_type || item.product && item.product.type || '';
    const title = item.product_title || item.title || item.product && item.product.title || '';
    return !/\b(e[\s-]?books?|audio[\s-]?books?|digital|downloads?|pdf)\b/i.test(type) && !/\b(e[\s-]?book|audio[\s-]?book|digital download|pdf download)\b/i.test(title);
  }

  function protectionItem(item, config) {
    return variantId(item) === config.id || Boolean(config.productId && productId(item) === config.productId);
  }

  function protectionPlan(cart, config, preference) {
    if (!config.id) return { updates: {}, add: false };
    const lines = cart.items.filter(function (item) { return protectionItem(item, config); });
    const keep = lines.find(function (item) { return variantId(item) === config.id; });
    const hasGoods = cart.items.some(function (item) {
      return !protectionItem(item, config) && shippableItem(item, config.lineMeta);
    });
    const wanted = hasGoods && config.available !== false && (preference === null ? config.defaultOn : preference);
    const updates = {};
    lines.forEach(function (item) {
      const quantity = wanted && item === keep ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    return { updates: updates, add: wanted && !keep };
  }

  function endpoint(win, operation) {
    const names = { add: 'cart_add_url', change: 'cart_change_url', update: 'cart_update_url', cart: 'cart_url' };
    const root = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
    let path = win.routes && win.routes[names[operation]];
    if (!path) path = root.replace(/\/?$/, '/') + 'cart' + (operation === 'cart' ? '' : '/' + operation);
    const url = new URL(path, win.location.origin);
    if (!url.pathname.endsWith('.js')) url.pathname += '.js';
    return url.pathname + url.search;
  }

  function createController(drawer, win) {
    const document = win.document;
    const originalOpen = typeof drawer.open === 'function' ? drawer.open.bind(drawer) : function () {};
    const originalClose = typeof drawer.close === 'function' ? drawer.close.bind(drawer) : function () {};
    const originalRender = typeof drawer.renderContents === 'function' ? drawer.renderContents.bind(drawer) : function () {};
    const originalSections = typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender.bind(drawer) : function () { return []; };
    const trustedTemplate = drawer.dataset.tbcTemplate;
    const state = { cart: null, config: {}, preference: null, storageKey: '', pending: 0, blocking: 0, failed: false, tail: Promise.resolve(), allowCheckout: false, focusTarget: null,
      markupScript: null, externalRevision: 0, externalSync: null, forceRefresh: false, visibleSignature: '', strings: {}, currentJob: null,
      closed: !drawer.classList.contains('active'), phase: drawer.classList.contains('active') ? 'open' : 'closed', animationGeneration: 0, openFrames: [], closeCleanup: null,
      offer: null, offerPromise: null, offerSequence: 0, offerAttempts: new Map(), addIntent: null, addProof: null, addProofPromise: null, addProofAgain: false };
    const originalDisabled = new WeakMap();
    let visibilityObserver = null;
    let unsubscribeAdd = null;

    function markupConfig() {
      const script = drawer.querySelector('[data-tbc-config]');
      return script ? JSON.parse(script.textContent) : {};
    }

    function isScoped() {
      if (drawer.isConnected === false || trustedTemplate !== 'danielmogs2' || drawer.dataset.tbcTemplate !== trustedTemplate) return false;
      try {
        const config = markupConfig();
        return (config.template === undefined ? drawer.dataset.tbcTemplate : config.template) === 'danielmogs2';
      }
      catch (_) { return false; }
    }

    function assertScope() {
      if (!isScoped()) {
        const error = new Error('The test drawer is no longer active.');
        error.tbcScopeExited = true;
        throw error;
      }
    }

    function readMarkup() {
      const script = drawer.querySelector('[data-tbc-cart-data]');
      state.cart = cartValue(JSON.parse(script ? script.textContent : '{}'));
      state.markupScript = script;
      state.visibleSignature = cartSignature(state.cart);
      const config = markupConfig();
      const id = String(config.protectionVariantId === undefined ? drawer.dataset.tbcProtectionVariantId || '' : config.protectionVariantId || '');
      const protectionProductId = String(config.protectionProductId === undefined ? drawer.dataset.tbcProtectionProductId || '' : config.protectionProductId || '');
      const metadataScript = drawer.querySelector('[data-tbc-line-meta]');
      const metadata = metadataScript ? JSON.parse(metadataScript.textContent) : [];
      if (!Array.isArray(metadata)) throw new Error('The cart classification response is incomplete.');
      state.config = {
        id: id && /^\d+$/.test(id) ? id : '',
        productId: /^\d+$/.test(protectionProductId) ? protectionProductId : '',
        lineMeta: metadata,
        available: config.protectionAvailable === undefined ? drawer.dataset.tbcProtectionAvailable !== 'false' : config.protectionAvailable === true,
        defaultOn: config.protectionDefault === undefined ? drawer.dataset.tbcProtectionDefault !== 'false' : config.protectionDefault === true
      };
      const storageKey = 'tbc-cart-protection:' + win.location.hostname + ':' + (state.config.productId || state.config.id);
      if (storageKey !== state.storageKey) state.preference = null;
      state.storageKey = storageKey;
      try {
        const legacyKey = 'tbc-cart-protection:' + win.location.hostname + ':' + state.config.id;
        const stored = win.sessionStorage.getItem(state.storageKey) || win.sessionStorage.getItem(legacyKey);
        state.preference = stored === 'false' ? false : stored === 'true' ? true : null;
        if (state.preference !== null && legacyKey !== storageKey) win.sessionStorage.setItem(storageKey, String(state.preference));
      } catch (_) {  }
      const labels = drawer.querySelector('[data-tbc-strings]');
      if (labels) state.strings = JSON.parse(labels.textContent);
      syncCheckbox();
    }

    function setPreference(value) {
      state.preference = value;
      try {
        if (value === null) win.sessionStorage.removeItem(state.storageKey);
        else win.sessionStorage.setItem(state.storageKey, String(value));
      } catch (_) {  }
    }

    function syncCheckbox() {
      const input = drawer.querySelector('[data-tbc-protection]');
      if (input) input.checked = Boolean(state.config.id && state.cart && state.cart.items.some(function (item) {
        return protectionItem(item, state.config) && item.quantity > 0;
      }));
    }

    function strings() {
      try {
        const script = drawer.querySelector('[data-tbc-strings]');
        return script ? JSON.parse(script.textContent) : state.strings;
      } catch (_) { return state.strings; }
    }

    function actionError(message) {
      const error = new Error(message);
      error.tbcUserMessage = true;
      return error;
    }

    function showError(error) {
      let node = drawer.querySelector('[data-tbc-error]');
      if (!node) {
        node = document.createElement('p');
        node.setAttribute('data-tbc-error', '');
        node.setAttribute('role', 'alert');
        (drawer.querySelector('.drawer__footer') || drawer.querySelector('.drawer__inner') || drawer).appendChild(node);
      }
      node.hidden = false;
      node.textContent = error && error.tbcUserMessage ? error.message : strings().error || 'Your cart could not be updated. Please try again.';
      let retry = drawer.querySelector('[data-tbc-action="retry"]');
      if (!retry) {
        retry = document.createElement('button');
        retry.setAttribute('type', 'button');
        retry.setAttribute('data-tbc-action', 'retry');
        retry.className = 'tbc-retry';
        retry.textContent = strings().retry || 'Try again';
        (drawer.querySelector('.drawer__footer') || drawer.querySelector('.drawer__inner') || drawer).appendChild(retry);
      }
      retry.hidden = false;
    }

    function clearError() {
      const node = drawer.querySelector('[data-tbc-error]');
      if (node) { node.hidden = true; node.textContent = ''; }
      const retry = drawer.querySelector('[data-tbc-action="retry"]');
      if (retry) retry.hidden = true;
    }

    function setBusy(busy) {
      drawer.setAttribute('aria-busy', String(busy));
      drawer.classList.toggle('tbc-cart--busy', busy);
      drawer.querySelectorAll('[data-tbc-action], [data-tbc-protection], [data-tbc-checkout]').forEach(function (control) {
        if (!originalDisabled.has(control)) originalDisabled.set(control, control.disabled);
        control.disabled = busy || originalDisabled.get(control) || Boolean(state.failed && control.matches('[data-tbc-checkout]'));
      });
      const status = drawer.querySelector('[data-tbc-status]');
      if (status) status.textContent = busy ? strings().loading || 'Updating your cart.' : state.failed ? '' : strings().updated || 'Cart updated.';
    }

    function rememberFocus() {
      const focus = document.activeElement;
      if (!focus || !drawer.contains(focus)) return null;
      const data = focus.dataset || {};
      if (data.tbcAction) {
        const peers = Array.from(drawer.querySelectorAll('[data-tbc-action]')).filter(function (node) { return node.dataset.tbcAction === data.tbcAction; });
        const matches = peers.filter(function (node) { return node.dataset.key === data.key && node.dataset.variantId === data.variantId; });
        return { kind: 'action', action: data.tbcAction, key: data.key, variantId: data.variantId,
          occurrence: matches.indexOf(focus), index: peers.indexOf(focus) };
      }
      if (focus.matches('[data-tbc-protection]')) return { kind: 'protection' };
      if (focus.matches('[data-tbc-checkout]')) return { kind: 'checkout' };
      if (focus === drawer.querySelector('.drawer__close')) return { kind: 'close' };
      return null;
    }

    function focusReplacement(target) {
      if (!target) return null;
      if (target.kind === 'action') {
        const peers = Array.from(drawer.querySelectorAll('[data-tbc-action]')).filter(function (node) {
          return node.dataset.tbcAction === target.action && !node.disabled && !node.hidden;
        });
        const matches = peers.filter(function (node) { return node.dataset.key === target.key && node.dataset.variantId === target.variantId; });
        return matches[target.occurrence] || matches[0] || peers[Math.min(target.index, peers.length - 1)];
      }
      const selectors = { protection: '[data-tbc-protection]', checkout: '[data-tbc-checkout]', close: '.drawer__close' };
      const replacement = drawer.querySelector(selectors[target.kind]);
      return replacement && !replacement.disabled && !replacement.hidden ? replacement : null;
    }

    function trapDrawerFocus(target) {
      if (!isScoped() || !drawer.classList.contains('active')) return;
      const replacement = target || drawer.querySelector('.drawer__close');
      if (typeof win.trapFocus === 'function') win.trapFocus(drawer.querySelector('.drawer__inner'), replacement);
      else if (replacement && typeof replacement.focus === 'function') replacement.focus();
    }

    function restoreFocus() {
      if (!state.focusTarget) return;
      const target = focusReplacement(state.focusTarget) || drawer.querySelector('[data-tbc-action="retry"]');
      const usable = target && !target.disabled && !target.hidden ? target : null;
      trapDrawerFocus(usable);
      state.focusTarget = null;
    }

    function setClosedState(closed) {
      state.closed = closed;
      drawer.dataset.tbcClosed = String(closed);
      drawer.setAttribute('aria-hidden', String(closed));
      drawer.inert = closed;
      if (closed) drawer.setAttribute('inert', '');
      else drawer.removeAttribute('inert');
    }

    function later(callback, delay) { return win.setTimeout ? win.setTimeout(callback, delay) : setTimeout(callback, delay); }
    function cancelLater(id) { if (win.clearTimeout) win.clearTimeout(id); else clearTimeout(id); }

    function cancelAnimation() {
      state.animationGeneration += 1;
      state.openFrames.forEach(function (frame) {
        if (frame.raf && typeof win.cancelAnimationFrame === 'function') win.cancelAnimationFrame(frame.id);
        else cancelLater(frame.id);
      });
      state.openFrames = [];
      if (state.closeCleanup) state.closeCleanup();
      state.closeCleanup = null;
      return state.animationGeneration;
    }

    function nextFrame(callback) {
      const raf = typeof win.requestAnimationFrame === 'function';
      const id = raf ? win.requestAnimationFrame(callback) : later(callback, 16);
      state.openFrames.push({ id: id, raf: raf });
    }

    function motionDuration(panel) {
      if (win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
      if (!panel || !win.getComputedStyle) return 300;
      const style = win.getComputedStyle(panel);
      function milliseconds(value) { return parseFloat(value || '0') * (String(value).trim().endsWith('ms') ? 1 : 1000); }
      const durations = String(style.transitionDuration || '0s').split(',').map(milliseconds);
      const delays = String(style.transitionDelay || '0s').split(',').map(milliseconds);
      return Math.max.apply(null, durations.map(function (duration, index) { return duration + delays[index % delays.length]; }));
    }

    function showDrawer(anchor) {
      const generation = cancelAnimation();
      if (anchor && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(anchor);
      setClosedState(false);
      state.phase = 'opening';
      drawer.dataset.tbcOpening = 'true';
      delete drawer.dataset.tbcClosing;
      drawer.classList.remove('active');
      drawer.classList.add('animate');
      document.body.classList.add('overflow-hidden');
      const timer = drawer.querySelector('countdown-timer[data-tbc-countdown]');
      if (timer && typeof timer.playTimer === 'function') timer.playTimer();
      nextFrame(function () {
        if (generation !== state.animationGeneration || !isScoped() || state.phase !== 'opening') return;
        nextFrame(function () {
          if (generation !== state.animationGeneration || !isScoped() || state.phase !== 'opening') return;
          state.phase = 'open';
          delete drawer.dataset.tbcOpening;
          drawer.classList.add('active');
          trapDrawerFocus();
        });
      });
    }

    function closeDrawer(nativeAlreadyClosed) {
      if (state.phase === 'closed') return;
      const animate = state.phase === 'open' || state.phase === 'closing';
      const generation = cancelAnimation();
      state.phase = 'closing';
      drawer.dataset.tbcClosing = 'true';
      delete drawer.dataset.tbcOpening;
      drawer.setAttribute('aria-hidden', 'true');
      drawer.inert = true;
      drawer.setAttribute('inert', '');
      if (nativeAlreadyClosed) {
        document.body.classList.remove('overflow-hidden');
        if (typeof win.removeTrapFocus === 'function') win.removeTrapFocus(drawer.activeElement);
      } else originalClose();
      drawer.classList.remove('active');
      const panel = drawer.querySelector('.drawer__inner');
      let timer;
      function cleanup() {
        if (timer !== undefined) cancelLater(timer);
        if (panel && panel.removeEventListener) panel.removeEventListener('transitionend', ended);
      }
      function finish() {
        if (generation !== state.animationGeneration || state.phase !== 'closing') return;
        cleanup();
        state.closeCleanup = null;
        state.phase = 'closed';
        delete drawer.dataset.tbcClosing;
        drawer.classList.remove('active');
        setClosedState(true);
      }
      function ended(event) { if (event.target === panel && event.propertyName === 'transform') finish(); }
      const duration = animate ? motionDuration(panel) : 0;
      if (duration <= 0) { finish(); return; }
      state.closeCleanup = cleanup;
      if (panel && panel.addEventListener) panel.addEventListener('transitionend', ended);
      timer = later(finish, duration + 50);
    }

    function observeVisibility() {
      if (!isScoped()) return;
      if (state.phase === 'opening' || state.phase === 'closing') return;
      const active = drawer.classList.contains('active');
      if (state.addProofPromise || state.offerPromise) {
        if (active && state.closed) drawer.classList.remove('active');
        return;
      }
      if (active && state.closed && pendingAdd()) { drawer.classList.remove('active'); proveBaseAdd(); return; }
      if (!active && !state.closed) {
        closeDrawer(true);
      } else if (active && state.closed) {
        showDrawer();
        scheduleSynchronize({ cleanOpen: true });
      }
    }

    async function request(url, options) {
      assertScope();
      const abort = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      let timer;
      const operation = (async function () {
        const response = await win.fetch(url, Object.assign({ credentials: 'same-origin', cache: 'no-store' }, abort ? { signal: abort.signal } : {}, options));
        const json = await response.json();
        assertScope();
        if (!response.ok || json.status >= 400 || json.errors) {
          const detail = typeof json.description === 'string' ? json.description : typeof json.message === 'string' ? json.message : '';
          const error = new Error(detail || strings().error || 'Your cart could not be updated. Please try again.');
          error.tbcUserMessage = Boolean(detail);
          error.tbcStatus = Number(json.status || response.status || 0);
          throw error;
        }
        return json;
      })();
      const deadline = new Promise(function (_resolve, reject) {
        timer = later(function () {
          const error = new Error(strings().error || 'Your cart could not be updated. Please try again.');
          error.tbcTimeout = true;
          reject(error);
          if (abort) abort.abort();
        }, REQUEST_TIMEOUT_MS);
      });
      try { return await Promise.race([operation, deadline]); }
      finally { cancelLater(timer); }
    }

    async function readCart() {
      state.cart = cartValue(await request(endpoint(win, 'cart')));
      return state.cart;
    }

    async function post(operation, body) {
      if (state.currentJob && !state.currentJob.blocking) {
        state.currentJob.blocking = true;
        state.blocking += 1;
        setBusy(true);
      }
      const context = sectionContext(win.location, drawer.dataset.tbcTemplate);
      return request(endpoint(win, operation), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({}, body, { sections: SECTIONS, sections_url: context.pathname + context.search }))
      });
    }

    async function ensureProtection() {
      if (!state.config.id) return;
      let plan = protectionPlan(state.cart, state.config, state.preference);
      if (Object.keys(plan.updates).length) {
        await post('update', { updates: plan.updates });
        await readCart();
      }
      if (plan.add) {
        await post('add', { items: [{ id: state.config.id, quantity: 1 }] });
        await readCart();
      }
      plan = protectionPlan(state.cart, state.config, state.preference);
      if (plan.add || Object.keys(plan.updates).length) throw new Error('Shipping protection could not be synchronized. Please retry.');
    }

    async function refresh() {
      const url = sectionContext(win.location, drawer.dataset.tbcTemplate);
      url.searchParams.set('sections', SECTIONS.join(','));
      const sections = await request(url.pathname + url.search);
      if (typeof sections['cart-drawer'] !== 'string' || typeof sections['cart-icon-bubble'] !== 'string') {
        throw new Error('The cart could not be refreshed. Please retry.');
      }
      const parsed = new win.DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = parsed.querySelector('cart-drawer[data-tbc-cart]');
      const freshContent = fresh && fresh.querySelector('#CartDrawer');
      const currentContent = drawer.querySelector('#CartDrawer');
      const currentPanel = drawer.querySelector('.drawer__inner');
      const freshPanel = fresh && fresh.querySelector('.drawer__inner');
      const freshData = fresh && fresh.querySelector('[data-tbc-cart-data]');
      if (!freshContent || !currentContent || !freshData || !freshPanel) throw new Error('The test cart could not be refreshed. Please retry.');
      cartValue(JSON.parse(freshData.textContent));
      if (!state.focusTarget) state.focusTarget = rememberFocus();
      const oldTimer = drawer.querySelector('countdown-timer[data-tbc-countdown]');
      const scrollPositions = ['.tbc-body', '.tbc-footer'].map(function (selector) {
        const node = drawer.querySelector(selector);
        return { selector: selector, top: node ? node.scrollTop : 0, left: node ? node.scrollLeft : 0 };
      });
      if (currentPanel) {
        currentPanel.innerHTML = freshPanel.innerHTML;
        if (freshPanel.attributes && currentPanel.setAttribute) {
          Array.from(freshPanel.attributes).forEach(function (attribute) { currentPanel.setAttribute(attribute.name, attribute.value); });
        }
      } else currentContent.innerHTML = freshContent.innerHTML;
      const newTimer = drawer.querySelector('countdown-timer[data-tbc-countdown]');
      const sameDuration = oldTimer && newTimer && oldTimer.dataset.tbcCountdownDuration === newTimer.dataset.tbcCountdownDuration;
      if (sameDuration) {
        if (newTimer.playInterval) win.clearInterval(newTimer.playInterval);
        newTimer.replaceWith(oldTimer);
      } else if (oldTimer && oldTimer.playInterval) win.clearInterval(oldTimer.playInterval);
      Object.keys(fresh.dataset).forEach(function (key) {
        if (key.indexOf('tbc') === 0 && !['tbcClosed', 'tbcOpening', 'tbcClosing'].includes(key)) drawer.dataset[key] = fresh.dataset[key];
      });
      drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const bubbleDoc = new win.DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const bubbleContent = bubbleDoc.querySelector('#cart-icon-bubble') || bubbleDoc.querySelector('.shopify-section');
        if (bubbleContent) bubble.innerHTML = bubbleContent.innerHTML;
      }
      readMarkup();
      scrollPositions.forEach(function (position) {
        const node = drawer.querySelector(position.selector);
        if (node) { node.scrollTop = position.top; node.scrollLeft = position.left; }
      });
      setBusy(state.blocking > 0);
      const timer = drawer.querySelector('countdown-timer[data-tbc-countdown]');
      if (drawer.classList.contains('active') && timer && typeof timer.playTimer === 'function') timer.playTimer();
      if (state.blocking > 0) trapDrawerFocus();
      else if (state.focusTarget) restoreFocus();
      else trapDrawerFocus();
    }

    function enqueue(task, rollback, options) {
      if (!isScoped()) return Promise.resolve(false);
      const job = { blocking: !(options && options.quiet) };
      if (state.pending === 0 || job.blocking && state.blocking === 0) state.focusTarget = rememberFocus();
      state.pending += 1;
      if (job.blocking) state.blocking += 1;
      setBusy(state.blocking > 0);
      const queued = state.tail.catch(function () {}).then(async function () {
        state.currentJob = job;
        try {
          await task();
          state.failed = false;
          clearError();
          return true;
        } catch (error) {
          if (rollback) rollback();
          if (error.tbcScopeExited || !isScoped()) return false;
          if (options && options.onError) { await options.onError(error); return false; }
          if (error.tbcTimeout) syncCheckbox();
          else try { await refresh(); } catch (_) { syncCheckbox(); }
          state.failed = true;
          showError(error);
          return false;
        } finally {
          state.pending -= 1;
          if (job.blocking) state.blocking -= 1;
          state.currentJob = null;
          setBusy(state.blocking > 0);
          if (state.pending === 0) restoreFocus();
        }
      });
      state.tail = queued;
      return queued;
    }

    async function synchronize(options) {
      assertScope();
      if (!drawer.querySelector('[data-tbc-cart-data]')) await refresh();
      readMarkup();
      await readCart();
      const initialPlan = protectionPlan(state.cart, state.config, state.preference);
      if (options && options.cleanOpen && state.visibleSignature === cartSignature(state.cart) && !initialPlan.add && !Object.keys(initialPlan.updates).length) {
        syncCheckbox();
        return;
      }
      if (state.config.id && state.cart.items.some(function (item) { return !protectionItem(item, state.config) && item.quantity > 0 && !lineMetadata(item, state.config.lineMeta); })) await refresh();
      await ensureProtection();
      await refresh();
      const plan = protectionPlan(state.cart, state.config, state.preference);
      if (plan.add || Object.keys(plan.updates).length) {
        await ensureProtection();
        await refresh();
        const finalPlan = protectionPlan(state.cart, state.config, state.preference);
        if (finalPlan.add || Object.keys(finalPlan.updates).length) throw new Error('The cart changed while updating. Please retry.');
      }
    }

    function scheduleSynchronize(options) {
      state.externalRevision += 1;
      if (!(options && options.cleanOpen)) state.forceRefresh = true;
      if (state.externalSync) return state.externalSync;
      const job = enqueue(async function () {
        let revision;
        let attempts = 0;
        do {
          revision = state.externalRevision;
          const force = state.forceRefresh;
          state.forceRefresh = false;
          await synchronize({ cleanOpen: !force });
          attempts += 1;
        } while (revision !== state.externalRevision && isScoped() && attempts < 3);
        if (revision !== state.externalRevision && isScoped()) throw new Error('The cart changed while updating. Please retry.');
      }, null, { quiet: true });
      state.externalSync = job;
      job.then(function () { if (state.externalSync === job) state.externalSync = null; });
      return job;
    }

    function offerVariants(config) {
      const variants = Array.isArray(config.variants) ? config.variants : [{ id: config.variantId, available: config.available }];
      return variants.filter(function (variant) {
        if (!variant || variant.available !== true || variant.eligible === false || !/^\d+$/.test(String(variant.id || '')) || Number(variant.id) <= 0) return false;
        const rule = variant.quantityRule || {};
        const min = Number(rule.min === undefined ? 1 : rule.min);
        const max = rule.max === null || rule.max === undefined ? null : Number(rule.max);
        const step = Number(rule.increment || 1);
        return min <= 1 && (max === null || max >= 1) && step === 1;
      });
    }

    function postAddConfig() {
      const script = typeof document.querySelector === 'function' && document.querySelector('[data-tbc-post-add-config]');
      if (!script) return null;
      try {
        const config = JSON.parse(script.textContent);
        return config && config.template === 'danielmogs2' && config.enabled === true && config.configured !== false && !config.requiresSellingPlan && /^\d+$/.test(String(config.productId || '')) && Number(config.productId) > 0 && offerVariants(config).length ? config : null;
      } catch (_) { return null; }
    }

    function eligibleOffer(response, keepClosed) {
      if (keepClosed || !win.TBCPostAddOffer || typeof win.TBCPostAddOffer.show !== 'function' || !response || response.status >= 400 || response.errors || response.item_count !== undefined) return null;
      const config = postAddConfig();
      if (!config || !Array.isArray(config.baseVariantIds)) return null;
      const baseIds = config.baseVariantIds.map(String);
      const added = Array.isArray(response.items) ? response.items : [response];
      if (!added.some(function (item) { return item && Number(item.quantity) > 0 && baseIds.includes(variantId(item)); })) return null;
      const offerIds = offerVariants(config).map(function (variant) { return String(variant.id); });
      if (state.cart.items.some(function (item) { return item.quantity > 0 && (offerIds.includes(variantId(item)) || config.productId && productId(item) === String(config.productId)); })) return null;
      return config;
    }

    function baseQuantity(cart, ids) {
      return cart.items.reduce(function (sum, item) { return sum + (ids.includes(variantId(item)) ? Number(item.quantity) || 0 : 0); }, 0);
    }

    function captureAddIntent(form, host) {
      if (!isScoped() || !host || !host.classList || !host.classList.contains('main-product-form') || host.dataset && host.dataset.isCartUpsell === 'true') return;
      const config = postAddConfig();
      if (!config || !Array.isArray(config.baseVariantIds)) return;
      const ids = config.baseVariantIds.map(String);
      const input = host.formIdInput || form && form.querySelector && (form.querySelector('[name="id"]') || form.querySelector('[name=id]'));
      if (!input || !ids.includes(String(input.value))) return;
      if (state.addIntent && state.addIntent.form === form && Date.now() - state.addIntent.started < 300) return;
      state.addIntent = { form: form, ids: ids, quantity: baseQuantity(state.cart, ids), started: Date.now(), cancelled: false };
    }

    function onPdpSubmit(event) {
      const form = event.target;
      const host = form && form.closest && form.closest('product-form');
      captureAddIntent(form, host);
    }

    function onPdpClick(event) {
      if (event.target && event.target.closest && event.target.closest('#cart-icon-bubble')) { clearAddIntent(); return; }
      const button = event.target && event.target.closest && event.target.closest('button');
      if (!button) return;
      const additional = button.classList && button.classList.contains('main-product-atc');
      const host = button.closest && button.closest('product-form') || additional && typeof document.querySelector === 'function' && document.querySelector('product-form.main-product-form');
      if (!host || button !== host.submitButton && !additional) return;
      captureAddIntent(host.form || button.form, host);
    }

    function clearAddIntent() {
      state.addIntent = null;
      if (state.addProof) state.addProof.cancelled = true;
    }

    function pendingAdd() {
      return state.addIntent && !state.addIntent.cancelled && Date.now() - state.addIntent.started < 30000 && postAddConfig() && win.TBCPostAddOffer && typeof win.TBCPostAddOffer.show === 'function';
    }

    function proveBaseAdd(response) {
      if (state.addProofPromise) { state.addProofAgain = true; return state.addProofPromise; }
      const intent = state.addIntent;
      state.addProof = intent;
      let config = null;
      let added = false;
      const proof = enqueue(async function () {
        await readCart();
        if (!intent.cancelled && Date.now() - intent.started < 30000 && baseQuantity(state.cart, intent.ids) > intent.quantity) {
          added = true;
          config = eligibleOffer({ items: state.cart.items }, false);
          if (state.addIntent === intent) state.addIntent = null;
        }
      }, null, { quiet: true });
      const job = proof.then(function (success) {
        if (!isScoped()) return false;
        if (!success) { clearAddIntent(); if (state.phase === 'closed' || state.phase === 'closing') showDrawer(); return false; }
        if (!added && !intent.cancelled && state.addIntent === intent && pendingAdd()) return true;
        return config && !intent.cancelled ? showPostAddOffer(response || state.cart, config) : renderCart(false);
      }).finally(function () {
        if (state.addProof === intent) state.addProof = null;
        if (state.addProofPromise === job) state.addProofPromise = null;
        const again = state.addProofAgain;
        state.addProofAgain = false;
        if (again && isScoped() && pendingAdd()) proveBaseAdd();
      });
      state.addProofPromise = job;
      return job;
    }

    function renderCart(keepClosed) {
      if (!keepClosed && (state.phase === 'closed' || state.phase === 'closing')) showDrawer();
      return scheduleSynchronize();
    }

    function offerError(config, error, uncertain) {
      const message = error && error.tbcUserMessage ? error.message : config.strings && config.strings.error || strings().error || 'Your cart could not be updated. Please try again.';
      return { ok: false, message: message, uncertain: Boolean(uncertain), retryable: true };
    }

    function addOfferVariant(id) {
      const context = state.offer;
      const current = postAddConfig();
      id = String(id);
      if (!isScoped() || !context || context.continuing || !current || !offerVariants(current).some(function (variant) { return String(variant.id) === id; }) || !offerVariants(context.config).some(function (variant) { return String(variant.id) === id; })) {
        return Promise.resolve({ ok: false, retryable: false });
      }
      if (context.attempt && context.attempt.uncertain && context.attempt.id !== id) return Promise.resolve({ ok: false, uncertain: true, retryable: false });
      const record = context.attempt && context.attempt.id === id ? context.attempt : state.offerAttempts.get(id) || { id: id, token: context.token, uncertain: false };
      context.attempt = record;
      let result = offerError(context.config, null, false);
      function assertActiveOffer() {
        const config = postAddConfig();
        if (!isScoped() || state.offer !== context || context.continuing || !config || !offerVariants(config).some(function (variant) { return String(variant.id) === id; })) {
          throw actionError(context.config.strings && context.config.strings.error || strings().error || 'This product is unavailable.');
        }
      }
      function confirmedLine() {
        return state.cart.items.find(function (item) {
          return variantId(item) === id && item.quantity > 0 && item.properties && item.properties._tbc_post_add_offer === record.token;
        });
      }
      function confirmed() {
        record.uncertain = false;
        state.offerAttempts.delete(id);
        result = { ok: true };
      }
      return enqueue(async function () {
        assertActiveOffer();
        await readCart();
        assertActiveOffer();
        if (confirmedLine()) { confirmed(); return; }
        if (record.uncertain) { result = offerError(context.config, null, true); return; }
        if (state.cart.items.some(function (item) { return variantId(item) === id && item.quantity > 0; })) {
          result = { ok: true, alreadyInCart: true };
          return;
        }
        record.uncertain = true;
        state.offerAttempts.set(id, record);
        try {
          await post('add', { items: [{ id: id, quantity: 1, properties: { _tbc_post_add_offer: record.token } }] });
        } catch (error) {
          if (error.tbcStatus >= 400 && error.tbcStatus < 500) {
            record.uncertain = false;
            state.offerAttempts.delete(id);
          }
          throw error;
        }
        await readCart();
        if (confirmedLine()) confirmed();
        else result = offerError(context.config, null, true);
      }, null, { onError: async function (error) {
        if (record.uncertain) {
          try { await readCart(); if (confirmedLine()) { confirmed(); return; } } catch (_) {}
        }
        result = offerError(context.config, error, record.uncertain);
      } }).then(function () { return result; });
    }

    function showPostAddOffer(response, config) {
      const context = { config: config, token: Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-' + ++state.offerSequence, continuing: false, continuation: null };
      state.offer = context;
      function continueToCart() {
        if (context.continuation) return context.continuation;
        context.continuing = true;
        context.continuation = isScoped() ? renderCart(false) : Promise.resolve(false);
        return context.continuation;
      }
      const job = Promise.resolve().then(function () {
        if (!isScoped()) return false;
        return win.TBCPostAddOffer.show({ response: response, config: config, addVariant: function (id) {
          return state.offer === context ? addOfferVariant(id) : Promise.resolve({ ok: false, retryable: false });
        }, continueToCart: continueToCart });
      }).then(function (result) { return result === false && !context.continuing ? false : continueToCart(); }, continueToCart).finally(function () {
        if (state.offer === context) state.offer = null;
        if (state.offerPromise === job) state.offerPromise = null;
      });
      state.offerPromise = job;
      return job;
    }

    function observeMarkup() {
      if (visibilityObserver && drawer.isConnected !== false) visibilityObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      observeVisibility();
      if (state.addProofPromise || state.offerPromise) return;
      if (!isScoped() || drawer.querySelector('[data-tbc-cart-data]') === state.markupScript) return;
      if (!drawer.querySelector('[data-tbc-cart-data]')) {
        state.markupScript = null;
        scheduleSynchronize();
        return;
      }
      readMarkup();
      setBusy(state.blocking > 0);
      if (state.phase === 'open' || state.phase === 'opening') scheduleSynchronize();
    }

    function dispose() {
      clearAddIntent();
      if (unsubscribeAdd) { unsubscribeAdd(); unsubscribeAdd = null; }
      if (typeof document.removeEventListener === 'function') {
        document.removeEventListener('submit', onPdpSubmit, true);
        document.removeEventListener('click', onPdpClick, true);
      }
      cancelAnimation();
      if (visibilityObserver) visibilityObserver.disconnect();
      if (!state.closed) {
        const replacement = typeof document.querySelector === 'function' ? document.querySelector('cart-drawer.active') : null;
        if (!replacement || replacement === drawer) {
          document.body.classList.remove('overflow-hidden');
          if (typeof win.removeTrapFocus === 'function') win.removeTrapFocus(drawer.activeElement);
        }
        drawer.classList.remove('active');
        state.phase = 'closed';
        delete drawer.dataset.tbcOpening;
        delete drawer.dataset.tbcClosing;
        setClosedState(true);
      }
      const timer = drawer.querySelector('countdown-timer[data-tbc-countdown]');
      if (timer && timer.playInterval) {
        win.clearInterval(timer.playInterval);
        timer.playInterval = null;
        timer.isPlaying = false;
      }
    }

    function changeLine(key, quantity) {
      clearAddIntent();
      return enqueue(async function () {
        readMarkup();
        await readCart();
        const line = state.cart.items.find(function (item) { return item.key === key; });
        if (!line) { await synchronize(); return; }
        if (!Number.isInteger(quantity) || quantity < 0) throw actionError(strings().quantityError || 'This quantity is not available for this item.');
        const meta = lineMetadata(line, state.config.lineMeta) || {};
        const exact = state.config.lineMeta.find(function (entry) { return entry && entry.key === line.key; }) || {};
        function allowed(name, alias) {
          const actual = line.instructions && line.instructions[name];
          if (typeof actual === 'boolean') return actual;
          const nested = exact.instructions && exact.instructions[name];
          if (typeof nested === 'boolean') return nested;
          return exact[name] !== false && exact[alias] !== false;
        }
        if (quantity === 0 && !allowed('can_remove', 'canRemove')) throw actionError(strings().removeUnavailable || 'This item cannot be removed separately.');
        if (quantity > 0 && !allowed('can_update_quantity', 'canUpdateQuantity')) throw actionError(strings().quantityUnavailable || 'This item quantity cannot be changed separately.');
        const rule = line.quantity_rule || meta.quantityRule;
        if (quantity > 0 && rule) {
          const min = Number(rule.min || 0);
          const max = rule.max === null || rule.max === undefined ? null : Number(rule.max);
          const step = Number(rule.increment || 1);
          if (quantity < min || max !== null && quantity > max || step > 1 && (quantity - min) % step !== 0) throw actionError(strings().quantityError || 'This quantity is not available for this item.');
        }
        await post('change', { id: key, quantity: quantity });
        await synchronize();
      });
    }

    function addUpsell(id) {
      clearAddIntent();
      return enqueue(async function () {
        if (!/^\d+$/.test(String(id))) throw new Error('This product is unavailable.');
        await post('add', { items: [{ id: String(id), quantity: 1 }] });
        await synchronize();
      });
    }

    function changeProtection(wanted) {
      if (!isScoped()) return Promise.resolve(false);
      clearAddIntent();
      setPreference(Boolean(wanted));
      return enqueue(async function () {
        await synchronize();
      });
    }

    function onClick(event) {
      if (!isScoped()) return;
      const button = event.target.closest('[data-tbc-action]');
      if (!button || !drawer.contains(button)) return;
      event.preventDefault();
      if (button.disabled || state.blocking > 0) return;
      const action = button.dataset.tbcAction;
      clearAddIntent();
      if (action === 'quantity') changeLine(button.dataset.key, Number(button.dataset.quantity));
      else if (action === 'remove') changeLine(button.dataset.key, 0);
      else if (action === 'upsell') addUpsell(button.dataset.variantId);
      else if (action === 'retry') enqueue(synchronize);
    }

    function onChange(event) {
      if (!isScoped()) return;
      if (!event.target.matches('[data-tbc-protection]')) return;
      event.stopPropagation();
      if (state.blocking > 0) { syncCheckbox(); return; }
      changeProtection(event.target.checked);
    }

    function onSubmit(event) {
      if (!isScoped()) return;
      const form = event.target;
      if (!form.matches('#CartDrawer-Form')) return;
      clearAddIntent();
      if (state.allowCheckout) { state.allowCheckout = false; return; }
      event.preventDefault();
      event.stopPropagation();
      enqueue(async function () {
        await synchronize();
        state.failed = false;
        clearError();
        const currentForm = drawer.querySelector('#CartDrawer-Form');
        const checkout = drawer.querySelector('[data-tbc-checkout]');
        if (!currentForm || !checkout) throw new Error('Checkout is unavailable. Please retry.');
        setBusy(false);
        state.allowCheckout = true;
        currentForm.requestSubmit(checkout);
      });
    }

    function attach() {
      if (drawer[CONTROLLER_KEY]) return drawer[CONTROLLER_KEY];
      readMarkup();
      setClosedState(state.closed);
      drawer.addEventListener('click', onClick);
      drawer.addEventListener('change', onChange);
      drawer.addEventListener('submit', onSubmit, true);
      if (typeof document.addEventListener === 'function') {
        document.addEventListener('submit', onPdpSubmit, true);
        document.addEventListener('click', onPdpClick, true);
      }
      if (typeof win.subscribe === 'function') unsubscribeAdd = win.subscribe('cart-update', function (event) {
        if (isScoped() && event && event.source === 'product-form' && pendingAdd() && !state.offerPromise) proveBaseAdd();
      });
      drawer.getSectionsToRender = function () { return isScoped() ? [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }] : originalSections(); };
      drawer.renderContents = function (response, keepClosed) {
        if (!isScoped()) return drawer.isConnected === false ? Promise.resolve(false) : originalRender(response, keepClosed);
        const failedAdd = response && (response.status >= 400 || response.errors);
        if (failedAdd) clearAddIntent();
        if (state.addProofPromise) {
          if (!keepClosed && !failedAdd && pendingAdd()) state.addProofAgain = true;
          return state.addProofPromise;
        }
        if (state.offerPromise) return state.offerPromise;
        const config = eligibleOffer(response, keepClosed);
        if (config) { clearAddIntent(); return showPostAddOffer(response, config); }
        if (keepClosed) clearAddIntent();
        else if (!failedAdd && pendingAdd()) return proveBaseAdd(response);
        return renderCart(keepClosed);
      };
      drawer.open = function () {
        if (drawer.isConnected === false) return Promise.resolve(false);
        if (!isScoped()) return originalOpen.apply(null, arguments);
        if (state.offerPromise) return state.addProofPromise || state.offerPromise;
        if (arguments[0]) clearAddIntent();
        else if (state.addProofPromise) return state.addProofPromise;
        else if (pendingAdd()) return proveBaseAdd();
        if (state.phase === 'closed' || state.phase === 'closing') showDrawer(arguments[0]);
        else if (arguments[0] && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(arguments[0]);
        return scheduleSynchronize({ cleanOpen: true });
      };
      drawer.close = function () {
        if (drawer.isConnected === false) return;
        if (!isScoped()) return originalClose.apply(null, arguments);
        closeDrawer(false);
      };
      drawer.addEventListener('click', function (event) {
        if (!isScoped()) return;
        const closeButton = event.target.closest && event.target.closest('.drawer__close');
        if (event.target.id === 'CartDrawer-Overlay' || closeButton && drawer.contains(closeButton)) {
          event.preventDefault();
          event.stopPropagation();
          drawer.close();
        }
      }, true);
      if (typeof win.MutationObserver === 'function') {
        visibilityObserver = new win.MutationObserver(observeVisibility);
        visibilityObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
      if (drawer.classList.contains('active') && state.config.id && state.cart.items.length) scheduleSynchronize({ cleanOpen: true });
      drawer[CONTROLLER_KEY] = api;
      return api;
    }

    const api = { attach: attach, state: state, refresh: refresh, synchronize: synchronize, changeLine: changeLine,
      addUpsell: addUpsell, addOfferVariant: addOfferVariant, changeProtection: changeProtection, enqueue: enqueue, onSubmit: onSubmit, observeMarkup: observeMarkup, observeVisibility: observeVisibility, dispose: dispose };
    return api;
  }

  function install(win) {
    const bound = new WeakMap();
    const active = new Set();
    function bindDrawers() {
      active.forEach(function (drawer) {
        if (drawer.isConnected === false) {
          bound.get(drawer).dispose();
          active.delete(drawer);
        }
      });
      win.document.querySelectorAll('cart-drawer[data-tbc-cart]').forEach(function (drawer) {
        if (bound.has(drawer)) {
          active.add(drawer);
          try { bound.get(drawer).observeMarkup(); } catch (_) {}
          return;
        }
        try {
          bound.set(drawer, createController(drawer, win).attach());
          active.add(drawer);
        } catch (error) {
          drawer.querySelectorAll('[data-tbc-checkout]').forEach(function (button) { button.disabled = true; });
          const alert = drawer.querySelector('[data-tbc-error]');
          if (alert) {
            let localized = '';
            try {
              const script = drawer.querySelector('[data-tbc-strings]');
              localized = script && JSON.parse(script.textContent).error;
            } catch (_) {}
            alert.hidden = false;
            alert.textContent = localized || 'Your cart could not be updated. Please try again.';
          }
        }
      });
    }
    win.customElements.whenDefined('cart-drawer').then(function () {
      bindDrawers();
      const observer = new win.MutationObserver(function (changes) {
        if (changes.some(function (change) { return change.addedNodes.length || change.removedNodes.length; })) bindDrawers();
      });
      observer.observe(win.document.body, { childList: true, subtree: true });
    });
  }

  if (typeof window !== 'undefined' && window.document) install(window);
  else if (typeof module !== 'undefined' && module.exports) module.exports = {
    sectionContext: sectionContext, cartValue: cartValue, protectionPlan: protectionPlan, shippableItem: shippableItem, endpoint: endpoint, createController: createController
  };
}());
